import React, { useState, useEffect } from 'react';
import { FIREBASE_CONFIG } from '../config/Config';
import { VideoProvider } from '../utils/VideoProvider';
import { fetchAcademyClasses } from '../services/VideoClassroomRepository'; 

const appId = FIREBASE_CONFIG.projectId;

const VideoClassroom = ({ isPremium, onRequestUpgrade }) => {
    const [modules, setModules] = useState([]);
    const [activeModule, setActiveModule] = useState(null);
    const [activeLesson, setActiveLesson] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const modulesList = await fetchAcademyClasses();
                setModules(modulesList);
                
                if (modulesList.length > 0) {
                    setActiveModule(modulesList[0].id);
                    if (modulesList[0].lessons.length > 0) {
                        setActiveLesson(modulesList[0].lessons[0]);
                    }
                }
            } catch (e) { console.error(e); } 
            finally { setLoading(false); }
        };

        fetchClasses();
    }, []);

    if (loading) return <div className="text-center py-20"><i className="fas fa-circle-notch fa-spin text-3xl text-[#C5A059]"></i><p className="text-sm text-gray-500 mt-2 font-bold">Carregando Sala de Aula...</p></div>;

    if (modules.length === 0) return (
        <div className="flex flex-col items-center justify-center h-[50vh] text-center px-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300 text-2xl"><i className="fas fa-video-slash"></i></div>
            <h3 className="text-lg font-bold text-gray-700">Nenhuma aula disponível</h3>
            <p className="text-xs text-gray-500 mt-1">Acesse o Painel Admin para adicionar conteúdo.</p>
        </div>
    );

    // LÓGICA DE BLOQUEIO MESTRE (V3.0 - HARD LOCK NA SALA)
    // Se não for Premium, bloqueia TUDO, não apenas o player.
    if (!isPremium) {
        return (
            <div className="relative rounded-xl overflow-hidden border border-gray-300 shadow-xl bg-white h-[calc(100vh-140px)]">
                {/* Conteúdo Ofuscado (Fake UI) */}
                <div className="blur-md opacity-30 pointer-events-none select-none filter grayscale p-6 h-full flex gap-6">
                    <div className="flex-1 bg-black rounded-xl"></div>
                    <div className="w-80 bg-gray-100 rounded-xl"></div>
                </div>

                {/* Overlay de Venda (Lock Screen) */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a1e30]/95 to-[#20243B]/90 z-50 flex flex-col items-center justify-center text-center p-8">
                    <div className="w-20 h-20 bg-[#C5A059] rounded-full flex items-center justify-center shadow-2xl mb-6 animate-pulse border-4 border-white/10">
                        <i className="fas fa-crown text-white text-3xl"></i>
                    </div>
                    
                    <span className="bg-white/10 text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-widest mb-4 border border-white/20">
                        Área Restrita
                    </span>

                    <h3 className="text-4xl font-serif font-bold text-white mb-4 leading-tight">
                        Curso OAB 2ª Fase <span className="text-[#C5A059]">Premium</span>
                    </h3>
                    
                    <p className="text-gray-300 text-sm max-w-lg mb-10 font-medium leading-relaxed">
                        Tenha acesso ilimitado a todas as vídeo aulas, banco de peças práticas e correção individualizada pelo Laboratório de Mentoria.
                    </p>
                    
                    <button 
                        onClick={onRequestUpgrade} 
                        className="bg-[#C5A059] text-[#1a1e30] px-10 py-4 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-white transition-all shadow-2xl border-2 border-[#C5A059] transform hover:scale-105 flex items-center gap-3"
                    >
                        <i className="fas fa-unlock"></i> Desbloquear Acesso Completo
                    </button>
                </div>
            </div>
        );
    }

    // LÓGICA DE RESOLUÇÃO DE PLAYER
    const playerConfig = VideoProvider.resolve(activeLesson?.videoId, true); 

    return (
        <div className="flex flex-col lg:flex-row h-full gap-6 animate-fade-in pb-20">
            {/* PLAYER DE VÍDEO (ESQUERDA) */}
            <div className="flex-1 flex flex-col">
                <div className="bg-black rounded-xl overflow-hidden shadow-2xl aspect-video border border-gray-800 relative group bg-grid-slate-900">
                    
                    {activeLesson ? (
                        playerConfig.type === 'YOUTUBE' || playerConfig.type === 'VIMEO' ? (
                            <iframe 
                                className="w-full h-full" 
                                src={playerConfig.url} 
                                title="Video Player" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                            ></iframe>
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-white bg-[#1a1e30]">
                                <i className="fas fa-play-circle text-5xl mb-4 text-[#C5A059]"></i>
                                <p className="text-sm font-bold">Player Nativo (R2)</p>
                                <p className="text-xs text-gray-400 mt-1">Carregando buffer seguro...</p>
                            </div>
                        )
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500 bg-gray-900">
                            <i className="fas fa-play-circle text-4xl opacity-50"></i>
                        </div>
                    )}
                </div>

                <div className="mt-6 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-[#20243B] mb-2">{activeLesson?.title || "Selecione uma aula"}</h2>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                            <i className="fas fa-layer-group text-[#C5A059]"></i> Módulo: {modules.find(m => m.id === activeModule)?.title || "Geral"}
                        </p>
                    </div>
                    {/* BADGE DE TIER */}
                    {(activeLesson?.tier === 'PRO' || activeLesson?.type === 'PREMIUM') && <span className="bg-[#C5A059] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm flex items-center gap-1"><i className="fas fa-crown"></i> PRO</span>}
                </div>
                
                {/* DESCRIÇÃO DA AULA */}
                <div className="mt-4 p-6 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-600 leading-relaxed">
                    <h4 className="font-bold text-gray-700 uppercase text-xs mb-2 flex items-center gap-2"><i className="fas fa-align-left"></i> Resumo da Aula</h4>
                    {activeLesson?.description || "Sem descrição disponível."}
                </div>
            </div>

            {/* LISTA DE MÓDULOS (DIREITA) */}
            <div className="w-full lg:w-80 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-140px)]">
                <div className="p-4 bg-[#1a1e30] text-white border-b border-gray-700">
                    <h3 className="font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                        <i className="fas fa-list"></i> Conteúdo do Curso
                    </h3>
                </div>
                <div className="overflow-y-auto flex-1 p-2 space-y-2 custom-scrollbar bg-gray-50/50">
                    {modules.map((module) => (
                        <div key={module.id} className="mb-2">
                            <button 
                                onClick={() => setActiveModule(activeModule === module.id ? null : module.id)} 
                                className={`w-full flex items-center justify-between p-3 rounded-lg text-xs font-bold transition-colors border ${activeModule === module.id ? 'bg-white text-[#20243B] border-gray-200 shadow-sm' : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-100'}`}
                            >
                                <span className="truncate flex-1 text-left">{module.title}</span>
                                <i className={`fas fa-chevron-down transition-transform ${activeModule === module.id ? 'rotate-180' : ''}`}></i>
                            </button>
                            
                            {activeModule === module.id && (
                                <div className="mt-1 space-y-1 pl-2 animate-fade-in-down border-l-2 border-gray-200 ml-2">
                                    {module.lessons.map((lesson) => {
                                        const isProFeature = lesson.tier === 'PRO' || lesson.type === 'PREMIUM';
                                        const isActive = activeLesson?.id === lesson.id;
                                        
                                        // FIX V18.1: Ícone de Cadeado VISUAL na Lista (Mesmo se já tiver acesso, mostra que é item premium)
                                        return (
                                            <button 
                                                key={lesson.id} 
                                                onClick={() => setActiveLesson(lesson)} 
                                                className={`w-full text-left p-3 rounded-md text-xs flex items-center justify-between transition-all group border-b border-gray-50 last:border-0 ${isActive ? 'bg-[#C5A059] text-white shadow-md font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                <div className="flex items-center gap-3 truncate">
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isActive ? 'bg-white/20' : (isProFeature ? 'bg-orange-50 text-[#C5A059]' : 'bg-green-50 text-green-600')}`}>
                                                         <i className={`fas ${isActive ? 'fa-play' : (isProFeature ? 'fa-crown' : 'fa-play-circle')}`}></i>
                                                    </div>
                                                    <div className="flex flex-col truncate">
                                                        <span className="truncate">{lesson.title}</span>
                                                        {isProFeature && <span className="text-[8px] uppercase font-bold flex items-center gap-1 mt-0.5 opacity-80"><i className="fas fa-lock text-[8px]"></i> PREMIUM</span>}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VideoClassroom;